import type ArrayProxy from '@ember/array/proxy';
import { setProperties } from '@ember/object';
import Route from '@ember/routing/route';
import type RouterService from '@ember/routing/router-service';
import { service } from '@ember/service';
import type StoreService from '@ember-data/store';
import { format } from 'date-fns';
import { didCancel } from 'ember-concurrency';
import type Location from 'garaje/models/location';
import type LocationSubscription from 'garaje/models/location-subscription';
import type LocationsCapacity from 'garaje/models/locations-capacity';
import type Subscription from 'garaje/models/subscription';
import type User from 'garaje/models/user';
import type StateService from 'garaje/services/state';
import { fetchCapacity } from 'garaje/utils/locations-capacity';
import { hash } from 'rsvp';

import type WorkplaceEntriesController from '../controller';
import type { WorkplaceEntriesModel, WorkplaceEntriesRouteParams } from '../route';

interface WorkplaceEntriesIndexModel {
  blocklistContacts: ArrayProxy<User>;
  currentLocation: Location;
  entries: WorkplaceEntriesModel;
  idScanContacts: ArrayProxy<User>;
  loadEntries: WorkplaceEntriesController['loadEntriesRestartableTask'];
  locationsCapacity: LocationsCapacity | null | undefined;
  workplaceDays: WorkplaceEntriesController['workplaceDays'];
  workplaceSubscription: LocationSubscription | Subscription | null;
}

class WorkplaceEntriesIndexRoute extends Route {
  @service declare router: RouterService;
  @service declare state: StateService;
  @service declare store: StoreService;

  titleToken = '';

  async model(): Promise<WorkplaceEntriesIndexModel> {
    const { currentLocation, workplaceSubscription } = this.state;
    const { capacityLimitEnabled } = currentLocation;
    const blocklistContacts = currentLocation.blocklistContacts;
    const idScanContacts = currentLocation.idScanContacts;
    const initial_entries = <WorkplaceEntriesModel>this.modelFor('workplace.entries');
    // eslint-disable-next-line ember/no-controller-access-in-routes
    const controller = <WorkplaceEntriesController>this.controllerFor('workplace.entries');

    const props = {
      refresh: '',
      page: 0,
      limit: 25,
      totalLoadedEntries: 0,
      date: initial_entries.date,
      query: initial_entries.query,
      filter: initial_entries.filter,
      sort: initial_entries.sort,
      selectedFlow: initial_entries.selectedFlow,
    };

    setProperties(controller, props);
    await controller.loadBosses.perform();
    await controller.loadEntries.perform().catch((e) => {
      if (!didCancel(e)) {
        throw e;
      }
      return this.router.transitionTo('workplace.entries');
    });
    const entries = <WorkplaceEntriesModel>this.modelFor('workplace.entries');

    let { date } = <WorkplaceEntriesRouteParams>this.paramsFor('workplace.entries');
    date = date || format(new Date(), 'yyyy-MM-dd');

    let locationsCapacity = null;

    if (capacityLimitEnabled) {
      locationsCapacity = fetchCapacity(this.store, currentLocation, date);
    }

    return hash({
      blocklistContacts,
      currentLocation,
      entries,
      idScanContacts,
      locationsCapacity,
      workplaceSubscription,
      loadEntries: controller.loadEntriesRestartableTask,
      workplaceDays: controller.workplaceDays,
    });
  }
}

export default WorkplaceEntriesIndexRoute;
